import { queryOptions } from "@tanstack/react-query";
import api from "@/core/api/axios";
import {
  APIResponseBFF,
  APIResponseBFFData,
} from "@superagents/types/api/api.v5";
import { identifyUser } from "@/shared/lib/analytics";
import { shouldMaskUserSectorIdInOnboardingV2 } from "@/shared/utils/onboardingV2Storage";

export type BffUser = APIResponseBFFData<"bff::user">;

/** Durante o onboarding v2 o setor do usuário fica escondido do app. */
export const normalizeUserForOnboardingV2 = (userData: BffUser): BffUser => {
  if (
    shouldMaskUserSectorIdInOnboardingV2(userData.documentId) &&
    userData.sectorId
  ) {
    return {
      ...userData,
      sectorId: undefined,
    };
  }

  return userData;
};

// SA-1166: identifica o usuário no PostHog. A associação à Company (Group) NÃO
// fica aqui de propósito — isto roda só no queryFn/login, mas o onboarding
// entrega o companyId via `setQueryData` (não dispara queryFn). O vínculo do
// Group é feito por `useIdentifyActiveCompany`, que reage a qualquer mudança de
// `companyId` no cache do usuário.
export const identifyForAnalytics = async (userData: BffUser) => {
  await identifyUser(userData.documentId!, { email: userData.email });
};

export const userMeQueryKey = (token: string | undefined) =>
  ["user", token] as const;

/**
 * O usuário logado (`/user/me` sem setor), para o `AuthProvider` e para o
 * pré-carregamento do boot (SA-1452). Sem refetch no foco: perda de acesso e
 * troca de perfil chegam pelo socket e pelo 403, e o foco fazia um `/user/me`
 * (com o identify do analytics) a cada alt-tab depois de 5 min.
 */
export const userMeQueryOptions = (token: string | undefined) =>
  queryOptions({
    queryKey: userMeQueryKey(token),
    queryFn: async () => {
      if (!token) throw new Error("No token available");

      const response = await api().get<APIResponseBFF<"bff::user">>(
        "/user/me",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const userData = response.data?.data;
      if (!userData) {
        throw new Error("User data not found");
      }

      const normalizedUserData = normalizeUserForOnboardingV2(userData);

      // Identify user + company for analytics if not already identified
      await identifyForAnalytics(normalizedUserData);

      return normalizedUserData;
    },
    staleTime: 1000 * 60 * 5, // Cache do usuário por 5 minutos
    refetchOnWindowFocus: false,
  });
